import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { Award, GraduationCap, Loader2, Save, ChevronLeft, ChevronRight, Info, Eye, ChefHat, FileText, Search, Zap } from 'lucide-react';

const GradingPage = () => {
    const { getAuthHeaders, API } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]); // combined lessons + assessments + skill assessments
    const [students, setStudents] = useState([]);
    const [criteria, setCriteria] = useState([]);
    const [grades, setGrades] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState(null); // 'lesson' or 'assessment' or 'skill_assessment'
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [scores, setScores] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [groups, setGroups] = useState([]);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
    const [selectedYear, setSelectedYear] = useState('all');

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        if (selectedItem) {
            fetchStudentsAndGrades();
        }
    }, [selectedItem]);

    useEffect(() => {
        if (selectedStudent) {
            loadStudentGrade();
        }
    }, [selectedStudent, grades]);

    const fetchItems = async () => {
        try {
            const [lessonsRes, assessmentsRes, skillRes] = await Promise.all([
                axios.get(`${API}/lessons`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessments`, { headers: getAuthHeaders() }),
                axios.get(`${API}/skill-assessments`, { headers: getAuthHeaders() })
            ]);
            const lessons = lessonsRes.data.map(l => ({ ...l, _type: 'lesson' }));
            const assessments = assessmentsRes.data.map(a => ({ ...a, _type: 'assessment' }));
            const skillAssessments = skillRes.data.map(s => ({ ...s, _type: 'skill_assessment' }));
            setItems([...lessons, ...assessments, ...skillAssessments].sort((a, b) => (b.date || '').localeCompare(a.date || '')));
        } catch (error) {
            console.error('Failed to fetch items:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentsAndGrades = async () => {
        setLoading(true);
        setSelectedGroupFilter('all');
        try {
            const item = items.find(i => i.id === selectedItem);
            if (!item) return;
            setSelectedItemType(item._type);

            if (item._type === 'lesson') {
                const [criteriaRes, studentsRes, gradesRes, groupsRes] = await Promise.all([
                    axios.get(`${API}/criteria`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/lessons/${selectedItem}/enrolled-students`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/grades/lesson/${selectedItem}`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/lessons/${selectedItem}/groups`, { headers: getAuthHeaders() })
                ]);
                const lessonCriteria = criteriaRes.data.filter(c => item.criteria_ids?.includes(c.id));
                setCriteria(lessonCriteria);
                setStudents(studentsRes.data);
                setGroups(groupsRes.data);
                if (studentsRes.data.length > 0) { setSelectedStudent(studentsRes.data[0].id); setCurrentStudentIndex(0); }
                const gradesMap = {};
                gradesRes.data.forEach(g => { gradesMap[g.student_id] = g; });
                setGrades(gradesMap);
            } else if (item._type === 'skill_assessment') {
                // Navigate to skill assessment detail for grading
                setCriteria([]);
                const [saRes, gradesRes] = await Promise.all([
                    axios.get(`${API}/skill-assessments/${selectedItem}`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/skill-assessment-grades/${selectedItem}`, { headers: getAuthHeaders() })
                ]);
                const allStudentsRes = await axios.get(`${API}/students`, { headers: getAuthHeaders() });
                const enrolled = allStudentsRes.data.filter(s => saRes.data.enrolled_student_ids?.includes(s.id));
                setStudents(enrolled);
                setGroups(saRes.data.local_groups || []);
                if (enrolled.length > 0) { setSelectedStudent(enrolled[0].id); setCurrentStudentIndex(0); }
                const gradesMap = {};
                gradesRes.data.forEach(g => { gradesMap[g.student_id] = g; });
                setGrades(gradesMap);
            } else {
                // Assessment - navigate to detail page for full grading
                setCriteria([]);
                const [studentsRes, gradesRes, groupsRes] = await Promise.all([
                    axios.get(`${API}/assessments/${selectedItem}/enrolled-students`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/assessment-grades/${selectedItem}`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/assessments/${selectedItem}/groups`, { headers: getAuthHeaders() })
                ]);
                setStudents(studentsRes.data);
                setGroups(groupsRes.data);
                if (studentsRes.data.length > 0) { setSelectedStudent(studentsRes.data[0].id); setCurrentStudentIndex(0); }
                const gradesMap = {};
                gradesRes.data.forEach(g => { gradesMap[g.student_id] = g; });
                setGrades(gradesMap);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load grading data');
        } finally {
            setLoading(false);
        }
    };

    const loadStudentGrade = () => {
        const existingGrade = grades[selectedStudent];
        if (existingGrade) {
            const scoreMap = {};
            existingGrade.scores?.forEach(s => { scoreMap[s.criterion_id] = s.score; });
            setScores(scoreMap);
            setFeedback(existingGrade.feedback || '');
        } else {
            setScores({});
            setFeedback('');
        }
    };

    const setScore = (criterionId, score) => {
        setScores(prev => ({ ...prev, [criterionId]: score }));
    };

    const getRatingDescription = (criterion, score) => {
        if (!criterion.rating_descriptions) return null;
        return criterion.rating_descriptions.find(rd => rd.score === score);
    };

    const handleSave = async () => {
        if (!selectedItem || !selectedStudent) return;
        setSaving(true);
        try {
            if (selectedItemType === 'lesson') {
                const scoresList = criteria.map(c => ({ criterion_id: c.id, score: scores[c.id] || 0 }));
                await axios.post(`${API}/grades`, { lesson_id: selectedItem, student_id: selectedStudent, scores: scoresList, feedback }, { headers: getAuthHeaders() });
                const totalScore = scoresList.reduce((sum, s) => sum + s.score, 0);
                setGrades(prev => ({ ...prev, [selectedStudent]: { scores: scoresList, feedback, total_score: totalScore, max_possible: criteria.length * 5 } }));
            }
            toast.success('Grade saved successfully');
        } catch (error) {
            toast.error('Failed to save grade');
        } finally {
            setSaving(false);
        }
    };

    const navigateStudent = (direction) => {
        const newIndex = currentStudentIndex + direction;
        if (newIndex >= 0 && newIndex < students.length) {
            setCurrentStudentIndex(newIndex);
            setSelectedStudent(students[newIndex].id);
        }
    };

    const currentStudent = students.find(s => s.id === selectedStudent);
    const selectedItemData = items.find(i => i.id === selectedItem);
    const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
    const maxScore = criteria.length * 5;

    const getScoreColor = (score) => {
        const colors = { 0: 'bg-gray-100 border-gray-200 text-gray-500', 1: 'bg-red-100 border-red-300 text-red-700', 2: 'bg-orange-100 border-orange-300 text-orange-700', 3: 'bg-yellow-100 border-yellow-300 text-yellow-700', 4: 'bg-blue-100 border-blue-300 text-blue-700', 5: 'bg-green-100 border-green-300 text-green-700' };
        return colors[score] || 'bg-gray-100 border-gray-200 text-gray-500';
    };

    const getSelectedScoreColor = (score) => {
        const colors = { 0: 'bg-gray-500 border-gray-500 text-white', 1: 'bg-red-500 border-red-500 text-white', 2: 'bg-orange-500 border-orange-500 text-white', 3: 'bg-yellow-500 border-yellow-500 text-white', 4: 'bg-blue-500 border-blue-500 text-white', 5: 'bg-green-500 border-green-500 text-white' };
        return colors[score] || 'bg-gray-500 border-gray-500 text-white';
    };

    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const surnameA = (a.name || '').split(' ').pop().toLowerCase();
        const surnameB = (b.name || '').split(' ').pop().toLowerCase();
        return surnameA.localeCompare(surnameB);
    });

    const filteredItems = items.filter(i => {
        const matchesYear = selectedYear === 'all' || i.year === Number(selectedYear);
        const matchesSearch = !searchQuery || (i.title || '').toLowerCase().includes(searchQuery.toLowerCase());
        return matchesYear && matchesSearch;
    });

    return (
        <Layout>
            <TooltipProvider>
                <div className="space-y-6 animate-fade-in" data-testid="grading-page">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Grading</h1>
                        <p className="text-gray-500 mt-1">Award points and feedback for lessons, assessments, and skill assessments</p>
                    </div>

                    {/* Item Selector with Year Filter & Search */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1 max-w-sm">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search items..." className="pl-10" data-testid="grading-item-search" />
                            </div>
                            <div className="flex gap-2">
                                {['all', '1', '2', '3'].map(y => (
                                    <button key={y} onClick={() => setSelectedYear(y)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedYear === y ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        data-testid={`grading-year-filter-${y}`}>
                                        {y === 'all' ? 'All' : `Year ${y}`}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-3 items-end">
                            <div className="flex-1 max-w-md">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Item</label>
                                <Select value={selectedItem || undefined} onValueChange={(value) => { setSelectedItem(value); setSelectedStudent(null); setStudents([]); setCriteria([]); setGrades({}); }}>
                                    <SelectTrigger data-testid="grading-item-selector">
                                        <SelectValue placeholder="Choose an item..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {filteredItems.map(item => (
                                            <SelectItem key={item.id} value={item.id}>
                                                <span className="flex items-center gap-2">
                                                    {item._type === 'lesson' ? <ChefHat className="w-3.5 h-3.5 text-ica-navy inline" /> :
                                                     item._type === 'skill_assessment' ? <Zap className="w-3.5 h-3.5 text-amber-500 inline" /> :
                                                     <FileText className="w-3.5 h-3.5 text-ica-gold inline" />}
                                                    {item.title} (Year {item.year}) — {item.date}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {selectedItemType === 'assessment' && selectedItem && (
                                <Button variant="outline" onClick={() => navigate(`/assessments/${selectedItem}`)} data-testid="go-to-assessment-detail">
                                    <Eye className="w-4 h-4 mr-1" /> Open Assessment
                                </Button>
                            )}
                            {selectedItemType === 'skill_assessment' && selectedItem && (
                                <Button variant="outline" onClick={() => navigate(`/skill-assessments/${selectedItem}`)} data-testid="go-to-skill-assessment-detail">
                                    <Eye className="w-4 h-4 mr-1" /> Open Skill Assessment
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Assessment: show summary + link to detail page */}
                    {selectedItem && selectedItemType === 'assessment' && !loading && (
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">Enrolled Students ({students.length})</h3>
                                <Button onClick={() => navigate(`/assessments/${selectedItem}`)} className="bg-ica-gold hover:bg-[#a8803a]" data-testid="grade-in-assessment-btn">
                                    <Award className="w-4 h-4 mr-1" /> Grade in Assessment
                                </Button>
                            </div>
                            {groups.length > 0 && (
                                <div className="flex gap-1.5 flex-wrap mb-4">
                                    <button onClick={() => setSelectedGroupFilter('all')}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        data-testid="grading-group-filter-all">
                                        All ({students.length})
                                    </button>
                                    {groups.map(g => (
                                        <button key={g.id} onClick={() => setSelectedGroupFilter(g.id)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                            data-testid={`grading-group-filter-${g.id}`}>
                                            {g.name} ({(g.student_ids || []).filter(sid => students.some(s => s.id === sid)).length})
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="divide-y divide-gray-100">
                                {sortBySurname(selectedGroupFilter === 'all' ? students : students.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids?.includes(s.id))).map(s => {
                                    const hasGrade = !!grades[s.id];
                                    return (
                                        <div key={s.id} className="flex items-center justify-between py-3 group cursor-pointer hover:bg-gray-50 px-2 rounded-lg transition-colors"
                                            onClick={() => navigate(`/assessments/${selectedItem}`)}
                                            data-testid={`assessment-student-row-${s.id}`}>
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${hasGrade ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {hasGrade ? <Award className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{s.name}</p>
                                                    <p className="text-xs text-gray-500">{s.student_id}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasGrade ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {hasGrade ? `Score: ${grades[s.id].total_score || 0}` : 'Not graded'}
                                                </span>
                                                <Eye className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        </div>
                                    );
                                })}
                                {students.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No students enrolled</p>}
                            </div>
                        </div>
                    )}

                    {/* Lesson: show full grading interface */}
                    {selectedItem && selectedItemType === 'lesson' && selectedStudent && !loading && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h2 className="font-semibold text-gray-900 mb-3">Students</h2>
                                {groups.length > 0 && (
                                    <div className="flex gap-1.5 flex-wrap mb-3">
                                        <button onClick={() => setSelectedGroupFilter('all')}
                                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                            data-testid="lesson-grading-group-filter-all">
                                            All
                                        </button>
                                        {groups.map(g => (
                                            <button key={g.id} onClick={() => setSelectedGroupFilter(g.id)}
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                data-testid={`lesson-grading-group-filter-${g.id}`}>
                                                {g.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                    {sortBySurname(selectedGroupFilter === 'all' ? students : students.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids?.includes(s.id))).map((student, idx) => {
                                        const studentGrade = grades[student.id];
                                        const hasGrade = !!studentGrade;
                                        return (
                                            <button key={student.id} onClick={() => { setSelectedStudent(student.id); setCurrentStudentIndex(idx); }}
                                                data-testid={`student-btn-${student.id}`}
                                                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${selectedStudent === student.id ? 'bg-ica-gold/10 border border-ica-gold' : 'hover:bg-gray-50 border border-transparent'}`}>
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${hasGrade ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {hasGrade ? '✓' : idx + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">{student.name}</p>
                                                    <p className="text-xs text-gray-500">{student.student_id}</p>
                                                </div>
                                                {hasGrade && <span className="text-xs font-medium text-ica-gold">{studentGrade.total_score}/{studentGrade.max_possible}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-white rounded-lg border border-gray-200 p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 bg-ica-navy/10 rounded-full flex items-center justify-center"><GraduationCap className="w-7 h-7 text-ica-navy" /></div>
                                            <div>
                                                <h2 className="text-xl font-bold text-gray-900">{currentStudent?.name}</h2>
                                                <p className="text-gray-500">{currentStudent?.student_id} - Year {selectedItemData?.year}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button variant="outline" size="icon" onClick={() => navigateStudent(-1)} disabled={currentStudentIndex === 0} data-testid="prev-student-btn" title="Previous Student"><ChevronLeft className="w-4 h-4" /></Button>
                                            <span className="text-sm text-gray-500 min-w-[60px] text-center">{currentStudentIndex + 1} / {students.length}</span>
                                            <Button variant="outline" size="icon" onClick={() => navigateStudent(1)} disabled={currentStudentIndex === students.length - 1} data-testid="next-student-btn" title="Next Student"><ChevronRight className="w-4 h-4" /></Button>
                                        </div>
                                    </div>
                                </div>

                                {criteria.length > 0 ? (
                                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="font-semibold text-gray-900">Grading Criteria</h3>
                                            <div className="text-right">
                                                <p className="text-2xl font-bold text-ica-gold">{totalScore} / {maxScore}</p>
                                                <p className="text-sm text-gray-500">Total Score</p>
                                            </div>
                                        </div>
                                        <div className="space-y-8">
                                            {criteria.map((criterion) => {
                                                const selectedScore = scores[criterion.id];
                                                const selectedRating = getRatingDescription(criterion, selectedScore);
                                                return (
                                                    <div key={criterion.id} data-testid={`criterion-${criterion.id}`}>
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900">{criterion.name}</p>
                                                                {criterion.description && <p className="text-sm text-gray-500">{criterion.description}</p>}
                                                            </div>
                                                            <span className="text-sm font-medium text-ica-gold ml-4">{selectedScore !== undefined ? selectedScore : '-'} / 5</span>
                                                        </div>
                                                        <div className="flex gap-2 mb-2">
                                                            {[0, 1, 2, 3, 4, 5].map((score) => {
                                                                const rating = getRatingDescription(criterion, score);
                                                                const isSelected = selectedScore === score;
                                                                return (
                                                                    <Tooltip key={score}>
                                                                        <TooltipTrigger asChild>
                                                                            <button onClick={() => setScore(criterion.id, score)} data-testid={`score-${criterion.id}-${score}`}
                                                                                className={`w-12 h-12 rounded-lg font-bold text-lg transition-all duration-200 border-2 ${isSelected ? getSelectedScoreColor(score) + ' shadow-md transform scale-105' : getScoreColor(score) + ' hover:scale-105'}`}>
                                                                                {score}
                                                                            </button>
                                                                        </TooltipTrigger>
                                                                        {rating && <TooltipContent side="top" className="max-w-xs"><p className="font-semibold">{rating.label}</p><p className="text-sm text-gray-300">{rating.description}</p></TooltipContent>}
                                                                    </Tooltip>
                                                                );
                                                            })}
                                                        </div>
                                                        {selectedRating && (
                                                            <div className="flex items-start gap-2 p-3 bg-ica-gold/5 border border-ica-gold/20 rounded-lg mt-2">
                                                                <Info className="w-4 h-4 text-ica-gold mt-0.5 flex-shrink-0" />
                                                                <div><p className="text-sm font-medium text-ica-gold-dark">{selectedRating.label}</p><p className="text-sm text-gray-600">{selectedRating.description}</p></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                                        <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                        <p className="text-gray-500">No grading criteria assigned to this lesson</p>
                                    </div>
                                )}

                                <div className="bg-white rounded-lg border border-gray-200 p-6">
                                    <label className="block font-medium text-gray-900 mb-3">Additional Feedback</label>
                                    <Textarea data-testid="feedback-input" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Add any additional comments..." rows={4} className="resize-none" />
                                </div>

                                <div className="flex justify-end">
                                    <Button onClick={handleSave} disabled={saving || criteria.length === 0} data-testid="save-grade-btn" className="bg-ica-gold hover:bg-ica-gold-dark px-8" size="lg">
                                        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Grade</>}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedItem && loading && (
                        <div className="text-center py-16 bg-white rounded-lg border border-gray-200"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" /></div>
                    )}

                    {!selectedItem && !loading && (
                        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                            <Award className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                            <h3 className="text-base font-semibold text-gray-700 mb-1">Select a lesson or assessment</h3>
                            <p className="text-gray-500">Choose an item above to start grading</p>
                        </div>
                    )}
                </div>
            </TooltipProvider>
        </Layout>
    );
};

export default GradingPage;
